// Report Templates Service - Multiple report format templates
import reportChartService from './reportChartService';

const reportTemplatesService = {
  // Available report templates
  templates: [
    {
      id: 'executive-summary',
      name: 'Executive Summary',
      description: 'High-level summary for C-suite executives and board members',
      audience: 'Executive Leadership',
      sections: ['overview', 'riskScore', 'keyFindings', 'businessImpact', 'recommendations'],
      format: 'concise'
    },
    {
      id: 'technical-detailed',
      name: 'Technical Detailed Report',
      description: 'Comprehensive technical analysis for security teams',
      audience: 'Security Operations / Red Team',
      sections: ['all'],
      format: 'detailed'
    },
    {
      id: 'compliance-audit',
      name: 'Compliance Audit Report',
      description: 'Compliance-focused report mapping findings to standards',
      audience: 'Compliance / Audit Teams',
      sections: ['findings', 'controls', 'compliance', 'remediation'],
      format: 'structured'
    },
    {
      id: 'incident-response',
      name: 'Incident Response Report',
      description: 'Attack timeline and response analysis',
      audience: 'Incident Response Team',
      sections: ['timeline', 'attackPath', 'iocs', 'containment', 'lessons'],
      format: 'chronological'
    },
    {
      id: 'risk-assessment',
      name: 'Risk Assessment Report',
      description: 'Risk-focused analysis with scoring and prioritization',
      audience: 'Risk Management',
      sections: ['riskSummary', 'threatAnalysis', 'vulnerabilities', 'riskMatrix'],
      format: 'risk-focused'
    },
    {
      id: 'board-presentation',
      name: 'Board Presentation',
      description: 'Visual presentation format for board meetings',
      audience: 'Board of Directors',
      sections: ['highlights', 'metrics', 'trends', 'investment'],
      format: 'presentation'
    }
  ],

  // Generate Executive Summary Report
  generateExecutiveSummary(debrief, project) {
    return {
      title: 'Executive Summary: Security Assessment',
      subtitle: project.name,
      date: new Date().toISOString(),
      classification: 'CONFIDENTIAL',

      sections: [
        {
          title: 'Assessment Overview',
          content: this.formatOverview(debrief, project)
        },
        {
          title: 'Security Posture Score',
          content: this.formatSecurityScore(debrief)
        },
        {
          title: 'MITRE ATT&CK Kill Chain',
          content: this.formatKillChainSection(debrief)
        },
        {
          title: 'Critical Findings',
          content: this.formatCriticalFindings(debrief)
        },
        {
          title: 'Business Impact',
          content: this.formatBusinessImpact(debrief)
        },
        {
          title: 'Recommended Actions',
          content: this.formatExecutiveRecommendations(debrief)
        }
      ]
    };
  },

  // Generate Technical Report
  generateTechnicalReport(debrief, project) {
    return {
      title: 'Technical Security Assessment Report',
      subtitle: project.name,
      date: new Date().toISOString(),
      classification: 'CONFIDENTIAL - TECHNICAL',
      version: '1.0',

      sections: [
        {
          title: '1. Introduction',
          subsections: [
            { title: '1.1 Scope', content: project.description },
            { title: '1.2 Objectives', content: this.formatObjectives(project) },
            { title: '1.3 Methodology', content: this.formatMethodology() }
          ]
        },
        {
          title: '2. Attack Summary',
          content: this.formatAttackSummary(debrief)
        },
        {
          title: '3. MITRE ATT&CK Kill Chain',
          subsections: [
            { title: '3.1 Kill Chain Coverage', content: this.formatKillChainSection(debrief) },
            { title: '3.2 Technique Details', content: this.formatMitreAnalysis(debrief) }
          ]
        },
        {
          title: '4. Attack Path Analysis',
          content: this.formatAttackPath(debrief)
        },
        {
          title: '5. Detailed Findings',
          content: this.formatDetailedFindings(debrief)
        },
        {
          title: '6. Technique Details',
          content: this.formatTechniqueDetails(debrief)
        },
        {
          title: '7. Detection Gap Analysis',
          subsections: [
            { title: '7.1 Gap Overview', content: this.formatEnhancedGapAnalysis(debrief) },
            { title: '7.2 Detection Coverage', content: this.formatDetectionAnalysis(debrief) }
          ]
        },
        {
          title: '8. Recommendations',
          content: this.formatTechnicalRecommendations(debrief)
        },
        {
          title: '9. Appendices',
          subsections: [
            { title: 'A. Action Log', content: this.formatActionLog(debrief) },
            { title: 'B. IOCs', content: this.formatIOCs(debrief) },
            { title: 'C. Evidence', content: this.formatEvidence(debrief) }
          ]
        }
      ]
    };
  },

  // Generate Compliance Report
  generateComplianceReport(debrief, project, framework = 'NIST') {
    const controlMappings = this.mapFindingsToControls(debrief, framework);

    return {
      title: `${framework} Compliance Assessment Report`,
      subtitle: project.name,
      date: new Date().toISOString(),
      framework: framework,

      sections: [
        {
          title: 'Compliance Summary',
          content: this.formatComplianceSummary(controlMappings, framework)
        },
        {
          title: 'Control Findings',
          content: controlMappings
        },
        {
          title: 'Gap Analysis',
          content: this.formatComplianceGaps(controlMappings)
        },
        {
          title: 'Attack Kill Chain',
          content: this.formatKillChainSection(debrief)
        },
        {
          title: 'Remediation Plan',
          content: this.formatRemediationPlan(debrief, controlMappings)
        }
      ]
    };
  },

  // Generate Risk Assessment Report
  generateRiskAssessmentReport(debrief, project) {
    return {
      title: 'Risk Assessment Report',
      subtitle: project.name,
      date: new Date().toISOString(),

      sections: [
        {
          title: 'Risk Summary',
          content: this.formatRiskSummary(debrief)
        },
        {
          title: 'Threat Analysis',
          content: this.formatThreatAnalysis(debrief)
        },
        {
          title: 'Attack Kill Chain',
          content: this.formatKillChainSection(debrief)
        },
        {
          title: 'Vulnerability Assessment',
          content: this.formatVulnerabilityAssessment(debrief)
        },
        {
          title: 'Detection Gap Analysis',
          content: this.formatEnhancedGapAnalysis(debrief)
        },
        {
          title: 'Risk Matrix',
          content: this.formatRiskMatrix(debrief)
        },
        {
          title: 'Risk Treatment Plan',
          content: this.formatRiskTreatment(debrief)
        }
      ]
    };
  },

  // Generate Incident Response Report
  generateIncidentResponseReport(debrief, project) {
    return {
      title: 'Purple Team Exercise - Incident Response Analysis',
      subtitle: project.name,
      date: new Date().toISOString(),

      sections: [
        {
          title: 'Incident Timeline',
          content: this.formatIncidentTimeline(debrief)
        },
        {
          title: 'Attack Chain Reconstruction',
          content: this.formatAttackChain(debrief)
        },
        {
          title: 'Attack Kill Chain Visualization',
          content: this.formatKillChainSection(debrief)
        },
        {
          title: 'Indicators of Compromise',
          content: this.formatIOCsDetailed(debrief)
        },
        {
          title: 'Detection Analysis',
          content: this.formatDetectionAnalysis(debrief)
        },
        {
          title: 'Response Assessment',
          content: this.formatResponseAssessment(debrief)
        },
        {
          title: 'Lessons Learned',
          content: debrief.lessonsLearned
        }
      ]
    };
  },

  // Generate Board Presentation
  generateBoardPresentation(debrief, project, options = {}) {
    return {
      title: 'Security Assessment Results',
      subtitle: project.client,
      date: new Date().toISOString(),
      format: 'slides',

      slides: [
        {
          title: 'Security Assessment Overview',
          type: 'title',
          content: {
            headline: project.name,
            period: `${project.startDate} - ${project.endDate || 'Present'}`,
            classification: 'Board Confidential'
          }
        },
        {
          title: 'Key Metrics',
          type: 'metrics',
          content: this.formatKeyMetrics(debrief)
        },
        {
          title: 'Security Posture',
          type: 'scorecard',
          content: this.formatPostureScorecard(debrief)
        },
        {
          title: 'Top Risks',
          type: 'list',
          content: this.formatTopRisks(debrief)
        },
        {
          title: 'Critical Actions Required',
          type: 'action-items',
          content: this.formatCriticalActions(debrief)
        },
        {
          title: 'Investment Recommendations',
          type: 'table',
          content: this.formatInvestmentRecs(debrief)
        },
        {
          title: 'MITRE ATT&CK Kill Chain',
          type: 'heatmap',
          content: this.formatKillChainSection(debrief, options.theme || 'light')
        }
      ]
    };
  },

  // Helper methods for formatting
  formatOverview(debrief, project) {
    const dateFmt = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return `
A comprehensive security assessment was conducted for ${project.client} to evaluate the organization's defensive capabilities against real-world attack scenarios. The engagement spanned from ${new Date(project.startDate).toLocaleDateString('de-CH', dateFmt)} to ${project.endDate ? new Date(project.endDate).toLocaleDateString('de-CH', dateFmt) : 'present'}.

Key Statistics:
- Total Attack Simulations: ${debrief.statistics.totalActions}
- Success Rate (Attacker): ${Math.round(debrief.statistics.successRate * 100)}%
- Detection Rate (Defense): ${Math.round(debrief.statistics.detectionRate * 100)}%
- Systems Compromised: ${debrief.statistics.targetsCompromised} of ${debrief.statistics.totalTargets}
- Unique Techniques Tested: ${debrief.statistics.uniqueTechniques}
`;
  },

  formatSecurityScore(debrief) {
    const detectionRate = debrief.statistics.detectionRate;
    let score, rating;

    if (detectionRate >= 0.8) { score = 'A'; rating = 'Excellent'; }
    else if (detectionRate >= 0.6) { score = 'B'; rating = 'Good'; }
    else if (detectionRate >= 0.4) { score = 'C'; rating = 'Fair'; }
    else if (detectionRate >= 0.2) { score = 'D'; rating = 'Poor'; }
    else { score = 'F'; rating = 'Critical'; }

    return {
      score,
      rating,
      detectionRate: Math.round(detectionRate * 100),
      factors: [
        { name: 'Detection Coverage', value: Math.round(detectionRate * 100), weight: 40 },
        { name: 'Response Time', value: 'N/A', weight: 20 },
        { name: 'Prevention Controls', value: Math.round((debrief.statistics.results.blocked || 0) / debrief.statistics.totalActions * 100), weight: 25 },
        { name: 'Kill Chain Coverage', value: debrief.statistics.uniqueTactics * 7, weight: 15 }
      ]
    };
  },

  formatCriticalFindings(debrief) {
    const critical = debrief.findings.critical || [];
    const high = debrief.findings.high || [];

    return {
      summary: `${critical.length} critical and ${high.length} high-severity findings identified`,
      critical: critical.slice(0, 3).map(f => ({
        title: f.title,
        impact: f.impact,
        assets: f.affectedAssets?.length || 0
      })),
      high: high.slice(0, 3).map(f => ({
        title: f.title,
        impact: f.impact
      }))
    };
  },

  formatBusinessImpact(debrief) {
    const impacts = [];

    if (debrief.findings.critical.length > 0) {
      impacts.push({
        severity: 'Critical',
        description: 'Potential for complete system compromise and data breach',
        likelihood: 'High',
        financialImpact: 'Severe'
      });
    }

    if (debrief.statistics.successRate > 0.7) {
      impacts.push({
        severity: 'High',
        description: 'Significant gaps in security controls allow unauthorized access',
        likelihood: 'Medium-High',
        financialImpact: 'Significant'
      });
    }

    return impacts;
  },

  formatExecutiveRecommendations(debrief) {
    return debrief.recommendations
      .filter(r => r.priority === 'immediate' || r.priority === 'high')
      .slice(0, 5)
      .map(r => ({
        title: r.title,
        priority: r.priority,
        timeframe: r.timeframe || 'Immediate',
        estimatedInvestment: r.effort === 'high' ? 'High' : r.effort === 'medium' ? 'Medium' : 'Low'
      }));
  },

  formatMethodology() {
    return `
The assessment followed industry-standard methodologies including:
- MITRE ATT&CK Framework for technique classification
- PTES (Penetration Testing Execution Standard) for engagement structure
- OWASP Testing Guide for web application assessment
- NIST Cybersecurity Framework for control mapping

The purple team approach combined red team offensive techniques with blue team defensive monitoring to provide real-time feedback on detection capabilities.
`;
  },

  formatObjectives(project) {
    return `
Primary Objectives:
1. Evaluate effectiveness of current security controls
2. Identify detection gaps in security monitoring
3. Test incident response procedures
4. Validate network segmentation effectiveness
5. Assess vulnerability to advanced persistent threats

Scope: ${project.description || 'Full enterprise environment including network, endpoints, and cloud resources.'}
`;
  },

  formatAttackSummary(debrief) {
    return {
      totalActions: debrief.statistics.totalActions,
      results: debrief.statistics.results,
      successRate: `${Math.round(debrief.statistics.successRate * 100)}%`,
      detectionRate: `${Math.round(debrief.statistics.detectionRate * 100)}%`,
      duration: debrief.statistics.totalDuration,
      operators: Object.keys(debrief.statistics.actionsByOperator || {}).length,
      techniques: debrief.statistics.uniqueTechniques,
      tactics: debrief.statistics.uniqueTactics
    };
  },

  formatMitreAnalysis(debrief) {
    if (!debrief.mitreAnalysis) return [];

    return debrief.mitreAnalysis.map(tech => ({
      id: tech.technique,
      name: tech.name,
      tactic: tech.tactic,
      severity: tech.severity,
      attempts: tech.attempts,
      successRate: `${Math.round(tech.successRate * 100)}%`,
      detectionRate: `${Math.round(tech.detectionRate * 100)}%`,
      mitigation: this.getMitigationForTechnique(tech.technique)
    }));
  },

  getMitigationForTechnique(techniqueId) {
    const mitigations = {
      'T1003': 'Enable Credential Guard, restrict LSASS access',
      'T1078': 'Implement MFA, monitor account usage',
      'T1055': 'Enable exploit protection, application control',
      'T1021': 'Restrict remote access, network segmentation',
      'T1053': 'Monitor scheduled task creation, restrict permissions'
    };
    return mitigations[techniqueId] || 'Review MITRE ATT&CK mitigations';
  },

  formatAttackPath(debrief) {
    if (!debrief.attackPath) return null;

    return {
      completeness: `${debrief.attackPath.completeness}%`,
      phases: debrief.attackPath.phases,
      criticalPaths: debrief.attackPath.criticalPaths,
      recommendations: this.getAttackPathRecommendations(debrief.attackPath)
    };
  },

  getAttackPathRecommendations(attackPath) {
    const recs = [];

    if (attackPath.completeness > 70) {
      recs.push('Implement defense-in-depth with multiple detection layers');
    }

    if (attackPath.criticalPaths && attackPath.criticalPaths.length > 0) {
      recs.push('Address critical attack paths with immediate controls');
    }

    return recs;
  },

  formatDetailedFindings(debrief) {
    return {
      critical: debrief.findings.critical,
      high: debrief.findings.high,
      medium: debrief.findings.medium,
      low: debrief.findings.low,
      total: (debrief.findings.critical?.length || 0) +
             (debrief.findings.high?.length || 0) +
             (debrief.findings.medium?.length || 0) +
             (debrief.findings.low?.length || 0)
    };
  },

  formatTechniqueDetails(debrief) {
    return debrief.technicalDetails?.techniques || {};
  },

  formatTechnicalRecommendations(debrief) {
    return debrief.recommendations.map(rec => ({
      ...rec,
      implementation: this.getImplementationSteps(rec)
    }));
  },

  getImplementationSteps(recommendation) {
    const steps = recommendation.detailedSteps || [];
    return steps.length > 0 ? steps : ['Review recommendation details', 'Create implementation plan', 'Execute changes', 'Validate effectiveness'];
  },

  formatActionLog(debrief) {
    return debrief.timeline || [];
  },

  formatIOCs(debrief) {
    // Extract IOCs from actions
    const iocs = {
      ipAddresses: new Set(),
      domains: new Set(),
      fileHashes: new Set(),
      commands: new Set()
    };

    if (debrief.detailedActions) {
      debrief.detailedActions.forEach(action => {
        if (action.notes) {
          // Extract IP addresses
          const ips = action.notes.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g);
          if (ips) ips.forEach(ip => iocs.ipAddresses.add(ip));

          // Extract domains
          const domains = action.notes.match(/\b[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}\b/g);
          if (domains) domains.forEach(d => iocs.domains.add(d));
        }
      });
    }

    return {
      ipAddresses: Array.from(iocs.ipAddresses),
      domains: Array.from(iocs.domains),
      fileHashes: Array.from(iocs.fileHashes),
      commands: Array.from(iocs.commands)
    };
  },

  formatEvidence(debrief) {
    if (!debrief.detailedActions) return [];

    return debrief.detailedActions
      .filter(a => a.evidence && a.evidence.length > 0)
      .map(a => ({
        action: a.title,
        evidence: a.evidence
      }));
  },

  // Compliance mapping
  mapFindingsToControls(debrief, framework) {
    const mappings = {
      NIST: this.getNISTMapping(debrief),
      'CIS Controls': this.getCISMapping(debrief),
      'ISO 27001': this.getISO27001Mapping(debrief)
    };

    return mappings[framework] || mappings.NIST;
  },

  getNISTMapping(debrief) {
    const controls = [];

    if (debrief.findings.critical.length > 0 || debrief.statistics.successRate > 0.5) {
      controls.push({
        control: 'PR.AC-1',
        name: 'Identities and credentials are issued, managed, verified, revoked, and audited',
        status: 'Non-Compliant',
        finding: 'Credential-based attacks were successful',
        priority: 'High'
      });
    }

    if (debrief.statistics.detectionRate < 0.5) {
      controls.push({
        control: 'DE.AE-1',
        name: 'A baseline of network operations and expected data flows is established',
        status: 'Partial',
        finding: 'Detection rate below acceptable threshold',
        priority: 'High'
      });
    }

    return controls;
  },

  getCISMapping(debrief) {
    return [
      {
        control: 'CIS Control 1',
        name: 'Inventory and Control of Enterprise Assets',
        status: this.assessControlStatus(debrief, 1),
        finding: 'Asset inventory gaps identified'
      },
      {
        control: 'CIS Control 4',
        name: 'Secure Configuration of Enterprise Assets and Software',
        status: this.assessControlStatus(debrief, 4),
        finding: 'Configuration weaknesses exploited'
      }
    ];
  },

  getISO27001Mapping(debrief) {
    return [
      {
        control: 'A.12.6.1',
        name: 'Management of technical vulnerabilities',
        status: debrief.findings.critical.length > 0 ? 'Non-Conformity' : 'Conforming',
        finding: debrief.findings.critical.length > 0 ? 'Critical vulnerabilities exploited' : 'Adequate controls'
      }
    ];
  },

  assessControlStatus(debrief, controlNumber) {
    const successRate = debrief.statistics.successRate;
    if (successRate > 0.7) return 'Non-Compliant';
    if (successRate > 0.3) return 'Partial';
    return 'Compliant';
  },

  formatComplianceSummary(controls, framework) {
    const compliant = controls.filter(c => c.status === 'Compliant').length;
    const partial = controls.filter(c => c.status === 'Partial').length;
    const nonCompliant = controls.filter(c => c.status === 'Non-Compliant' || c.status === 'Non-Conformity').length;

    return {
      framework,
      totalControls: controls.length,
      compliant,
      partial,
      nonCompliant,
      complianceScore: Math.round((compliant + partial * 0.5) / controls.length * 100)
    };
  },

  formatComplianceGaps(controls) {
    return controls.filter(c => c.status !== 'Compliant');
  },

  formatRemediationPlan(debrief, controls) {
    const gaps = controls.filter(c => c.status !== 'Compliant');

    return gaps.map(gap => ({
      control: gap.control,
      name: gap.name,
      currentState: gap.status,
      targetState: 'Compliant',
      actions: this.getRemediationActions(gap),
      timeline: gap.priority === 'High' ? '30 days' : '90 days',
      owner: 'Security Team'
    }));
  },

  getRemediationActions(control) {
    return [
      'Conduct gap assessment',
      'Develop remediation plan',
      'Implement controls',
      'Validate implementation',
      'Update documentation'
    ];
  },

  // Risk Assessment formatting
  formatRiskSummary(debrief) {
    const riskLevel = debrief.executiveSummary?.overallRisk?.level || 'MEDIUM';

    return {
      overallRisk: riskLevel,
      criticalRisks: debrief.findings.critical.length,
      highRisks: debrief.findings.high.length,
      mediumRisks: debrief.findings.medium.length,
      lowRisks: debrief.findings.low.length,
      trending: 'Stable'
    };
  },

  formatThreatAnalysis(debrief) {
    return {
      threatsSimulated: debrief.statistics.totalActions,
      successfulThreats: debrief.statistics.results.successful || 0,
      topThreats: (debrief.mitreAnalysis?.slice(0, 5) || []).map(t => ({
        technique: t.technique,
        name: t.name,
        tactic: t.tactic,
        count: t.attempts,
        successRate: `${Math.round((t.successRate || 0) * 100)}%`
      })),
      threatActorProfile: 'Advanced Persistent Threat (APT)',
      attackVectors: this.getAttackVectors(debrief)
    };
  },

  getAttackVectors(debrief) {
    const vectors = new Set();

    if (debrief.mitreAnalysis) {
      debrief.mitreAnalysis.forEach(tech => {
        if (tech.tactic === 'Initial Access') vectors.add('External Attack Surface');
        if (tech.tactic === 'Lateral Movement') vectors.add('Internal Network');
        if (tech.tactic === 'Credential Access') vectors.add('Identity Infrastructure');
      });
    }

    return Array.from(vectors);
  },

  formatVulnerabilityAssessment(debrief) {
    return {
      totalVulnerabilities: debrief.findings.critical.length +
                           debrief.findings.high.length +
                           debrief.findings.medium.length,
      bySeverity: {
        critical: debrief.findings.critical.length,
        high: debrief.findings.high.length,
        medium: debrief.findings.medium.length,
        low: debrief.findings.low.length
      },
      topVulnerabilities: [
        ...debrief.findings.critical,
        ...debrief.findings.high
      ].slice(0, 5).map(f => ({
        title: f.title,
        severity: f.severity,
        impact: f.impact || '',
        affectedAssets: (f.affectedAssets || []).join(', ') || '-',
        remediation: f.remediation || 'See recommendations'
      }))
    };
  },

  formatRiskMatrix(debrief) {
    return {
      axes: {
        x: 'Likelihood',
        y: 'Impact'
      },
      risks: this.plotRisksOnMatrix(debrief)
    };
  },

  plotRisksOnMatrix(debrief) {
    const risks = [];

    debrief.findings.critical.forEach(f => {
      risks.push({ name: f.title, likelihood: 4, impact: 5, severity: 'critical' });
    });

    debrief.findings.high.forEach(f => {
      risks.push({ name: f.title, likelihood: 3, impact: 4, severity: 'high' });
    });

    return risks;
  },

  formatRiskTreatment(debrief) {
    return debrief.recommendations.map(rec => ({
      risk: rec.title,
      treatment: 'Mitigate',
      action: rec.description,
      priority: rec.priority,
      owner: 'Security Team',
      dueDate: rec.timeframe || '30 days'
    }));
  },

  // Incident Response formatting
  formatIncidentTimeline(debrief) {
    return debrief.timeline?.map((event, index) => ({
      sequence: index + 1,
      timestamp: event.timestamp,
      event: event.action,
      technique: event.technique,
      result: event.result,
      detected: event.result === 'detected' || event.result === 'blocked'
    })) || [];
  },

  formatAttackChain(debrief) {
    return debrief.attackPath?.phases?.map((phase, index) => ({
      step: index + 1,
      phase: phase.name,
      actions: phase.actionCount,
      successful: phase.successfulCount,
      keyActions: phase.keyActions
    })) || [];
  },

  formatIOCsDetailed(debrief) {
    const iocs = this.formatIOCs(debrief);

    return {
      ...iocs,
      techniques: debrief.mitreAnalysis?.map(t => t.technique) || [],
      recommendations: [
        'Add IOCs to threat intelligence platform',
        'Update SIEM detection rules',
        'Block identified malicious indicators'
      ]
    };
  },

  formatDetectionAnalysis(debrief) {
    return {
      totalEvents: debrief.statistics.totalActions,
      detected: debrief.statistics.results.detected || 0,
      blocked: debrief.statistics.results.blocked || 0,
      missed: debrief.statistics.results.successful || 0,
      detectionRate: `${Math.round(debrief.statistics.detectionRate * 100)}%`,
      meanTimeToDetect: 'N/A',
      detectionGaps: this.identifyDetectionGaps(debrief)
    };
  },

  formatEnhancedGapAnalysis(debrief) {
    const DETECTION_LEVELS = {
      0: { name: 'None', description: 'No detection capability' },
      1: { name: 'Basic', description: 'Basic/signature-based detection' },
      2: { name: 'Fair', description: 'Rule-based detection with some behavioral' },
      3: { name: 'Good', description: 'Behavioral detection with good coverage' },
      4: { name: 'Very Good', description: 'Advanced detection with correlation' },
      5: { name: 'Excellent', description: 'Comprehensive detection with threat hunting' }
    };

    const VISIBILITY_LEVELS = {
      0: { name: 'None', description: 'No visibility into technique execution' },
      1: { name: 'Minimal', description: 'Limited logging, may miss events' },
      2: { name: 'Basic', description: 'Basic event logging enabled' },
      3: { name: 'Good', description: 'Good event collection and retention' },
      4: { name: 'Excellent', description: 'Comprehensive logging and correlation' }
    };

    // Build technique map from actions
    const techniqueMap = new Map();

    if (debrief.actions && Array.isArray(debrief.actions)) {
      debrief.actions.forEach(action => {
        if (!action.mitreAttackTechnique?.id) return;

        const techId = action.mitreAttackTechnique.id;
        if (!techniqueMap.has(techId)) {
          techniqueMap.set(techId, {
            id: techId,
            name: action.mitreAttackTechnique.name,
            tactic: action.mitreAttackTechnique.tactic,
            attempts: 0,
            successful: 0,
            detected: 0,
            blocked: 0,
            partial: 0,
            failed: 0
          });
        }

        const entry = techniqueMap.get(techId);
        entry.attempts++;
        if (action.result === 'successful') entry.successful++;
        else if (action.result === 'detected') entry.detected++;
        else if (action.result === 'blocked') entry.blocked++;
        else if (action.result === 'partial') entry.partial++;
        else if (action.result === 'failed') entry.failed++;
      });
    }

    // Calculate gaps for each technique
    const gaps = Array.from(techniqueMap.values()).map(tech => {
      // Calculate detection rate
      const detectionRate = tech.attempts > 0
        ? ((tech.detected + tech.blocked) / tech.attempts) * 100
        : 0;

      // Determine detection level (0-5)
      let detectionLevel = 0;
      if (detectionRate >= 95) detectionLevel = 5;
      else if (detectionRate >= 80) detectionLevel = 4;
      else if (detectionRate >= 60) detectionLevel = 3;
      else if (detectionRate >= 40) detectionLevel = 2;
      else if (detectionRate >= 20) detectionLevel = 1;

      // Determine visibility level (0-4)
      let visibilityLevel = 0;
      if (tech.blocked > 0) visibilityLevel = 4;
      else if (tech.detected > 0) visibilityLevel = 3;
      else if (tech.partial > 0) visibilityLevel = 2;
      else if (tech.failed > 0) visibilityLevel = 1;

      // Calculate gap priority
      let gapPriority = 'low';
      let gapScore = 0;

      if (detectionLevel < 3) {
        if (tech.successful > 0) {
          gapPriority = 'critical';
          gapScore = 100 - (detectionLevel * 20) + (tech.successful * 5);
        } else if (detectionLevel < 2) {
          gapPriority = 'high';
          gapScore = 80 - (detectionLevel * 15);
        } else {
          gapPriority = 'medium';
          gapScore = 60 - (detectionLevel * 10);
        }
      }

      // Generate recommendations
      const recommendations = [];
      if (detectionLevel < 2) {
        recommendations.push(`Implement detection rules for ${tech.id}`);
      }
      if (visibilityLevel < 2) {
        recommendations.push(`Enhance logging for ${tech.name}`);
      }
      if (tech.successful > 0 && detectionLevel < 4) {
        recommendations.push(`Review prevention controls for ${tech.name}`);
      }

      return {
        technique: tech.id,
        name: tech.name,
        tactic: tech.tactic,
        attempts: tech.attempts,
        successful: tech.successful,
        detected: tech.detected,
        blocked: tech.blocked,
        detectionRate: Math.round(detectionRate),
        detectionLevel: {
          level: detectionLevel,
          name: DETECTION_LEVELS[detectionLevel].name,
          description: DETECTION_LEVELS[detectionLevel].description
        },
        visibilityLevel: {
          level: visibilityLevel,
          name: VISIBILITY_LEVELS[visibilityLevel].name,
          description: VISIBILITY_LEVELS[visibilityLevel].description
        },
        gapPriority,
        gapScore,
        recommendations
      };
    });

    // Sort by gap score (highest first)
    gaps.sort((a, b) => b.gapScore - a.gapScore);

    // Calculate summary statistics
    const criticalGaps = gaps.filter(g => g.gapPriority === 'critical').length;
    const highGaps = gaps.filter(g => g.gapPriority === 'high').length;
    const mediumGaps = gaps.filter(g => g.gapPriority === 'medium').length;

    const avgDetectionLevel = gaps.length > 0
      ? gaps.reduce((sum, g) => sum + g.detectionLevel.level, 0) / gaps.length
      : 0;

    const avgVisibilityLevel = gaps.length > 0
      ? gaps.reduce((sum, g) => sum + g.visibilityLevel.level, 0) / gaps.length
      : 0;

    return {
      summary: {
        totalTechniques: gaps.length,
        criticalGaps,
        highGaps,
        mediumGaps,
        avgDetectionLevel: avgDetectionLevel.toFixed(1),
        avgDetectionLevelName: DETECTION_LEVELS[Math.round(avgDetectionLevel)].name,
        avgVisibilityLevel: avgVisibilityLevel.toFixed(1),
        avgVisibilityLevelName: VISIBILITY_LEVELS[Math.round(avgVisibilityLevel)].name
      },
      gaps: gaps,
      topGaps: gaps.slice(0, 10), // Top 10 priority gaps
      detectionLevels: DETECTION_LEVELS,
      visibilityLevels: VISIBILITY_LEVELS
    };
  },

  identifyDetectionGaps(debrief) {
    const gaps = [];

    if (debrief.mitreAnalysis) {
      debrief.mitreAnalysis.forEach(tech => {
        if (tech.detectionRate < 0.5) {
          gaps.push({
            technique: tech.technique,
            name: tech.name,
            detectionRate: `${Math.round(tech.detectionRate * 100)}%`,
            recommendation: this.getMitigationForTechnique(tech.technique)
          });
        }
      });
    }

    return gaps;
  },

  formatResponseAssessment(debrief) {
    return {
      containmentEffectiveness: debrief.statistics.results.blocked > 0 ? 'Partial' : 'Limited',
      responseTime: 'N/A',
      escalationProcedures: 'Tested',
      recommendations: [
        'Implement automated containment playbooks',
        'Improve detection-to-response time',
        'Enhance communication procedures'
      ]
    };
  },

  // Board Presentation formatting
  formatKeyMetrics(debrief) {
    return [
      { label: 'Attack Simulations', value: debrief.statistics.totalActions, trend: 'neutral' },
      { label: 'Detection Rate', value: `${Math.round(debrief.statistics.detectionRate * 100)}%`, trend: debrief.statistics.detectionRate > 0.6 ? 'positive' : 'negative' },
      { label: 'Critical Findings', value: debrief.findings.critical.length, trend: 'negative' },
      { label: 'Systems Tested', value: debrief.statistics.totalTargets, trend: 'neutral' }
    ];
  },

  formatPostureScorecard(debrief) {
    const score = this.formatSecurityScore(debrief);

    return {
      overallGrade: score.score,
      rating: score.rating,
      components: score.factors,
      businessImpact: this.getBusinessImpactBullets(debrief, score.score)
    };
  },

  getBusinessImpactBullets(debrief, grade) {
    const bullets = [];
    const tacticsUsed = new Set((debrief.mitreAnalysis || []).filter(t => t.successRate > 0.5).map(t => t.tactic));
    const detectionRate = debrief.statistics.detectionRate;

    // Financial Risk
    if (tacticsUsed.has('Collection') || tacticsUsed.has('Exfiltration') || tacticsUsed.has('Impact')) {
      bullets.push({ type: 'Financial Risk', text: 'Data exfiltration or impact tactics succeeded — potential for direct financial loss or ransom demands.' });
    }

    // Reputation Risk
    if (tacticsUsed.has('Exfiltration') && detectionRate < 0.5) {
      bullets.push({ type: 'Reputation Risk', text: 'Data exfiltration possible with low detection — a breach could damage customer trust and brand reputation.' });
    }

    // Compliance Risk
    if (detectionRate < 0.4) {
      bullets.push({ type: 'Compliance Risk', text: `Detection rate of ${Math.round(detectionRate * 100)}% is below regulatory expectations (GDPR, FINMA, ISO 27001).` });
    }

    // Operational Risk
    if (grade === 'D' || grade === 'F') {
      bullets.push({ type: 'Operational Risk', text: 'Current security posture may lead to prolonged outages or business interruption during a real incident.' });
    }

    return bullets;
  },

  formatTopRisks(debrief) {
    const severityOrder = { Critical: 0, High: 1 };
    const risks = [
      ...debrief.findings.critical.map(f => ({ severity: 'Critical', description: f.title, assets: f.affectedAssets?.length || 0 })),
      ...debrief.findings.high.map(f => ({ severity: 'High', description: f.title, assets: f.affectedAssets?.length || 0 }))
    ];
    risks.sort((a, b) => {
      const sevDiff = (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9);
      if (sevDiff !== 0) return sevDiff;
      return b.assets - a.assets;
    });
    return risks.slice(0, 5);
  },

  formatCriticalActions(debrief) {
    return debrief.recommendations
      .filter(r => r.priority === 'immediate')
      .slice(0, 5)
      .map(r => ({
        action: r.title,
        owner: 'CISO',
        deadline: 'Immediate',
        status: 'Pending'
      }));
  },

  formatInvestmentRecs(debrief) {
    const investments = [];

    if (debrief.statistics.detectionRate < 0.5) {
      investments.push({
        area: 'Detection & Response',
        recommendation: 'EDR/XDR Enhancement',
        priority: 'High',
        estimatedROI: 'Risk Reduction: 40%'
      });
    }

    if (debrief.findings.critical.length > 0) {
      investments.push({
        area: 'Vulnerability Management',
        recommendation: 'Accelerated Patching Program',
        priority: 'Critical',
        estimatedROI: 'Risk Reduction: 30%'
      });
    }

    return investments;
  },

  // Generate MITRE heatmap SVG for embedding in reports
  formatMitreHeatmapSection(debrief, theme = 'light') {
    const techniques = (debrief.mitreAnalysis || []).map(tech => ({
      technique: tech.technique,
      id: tech.technique,
      name: tech.name,
      tactic: tech.tactic,
      attempts: tech.attempts,
      count: tech.attempts,
      successRate: tech.successRate || 0,
      detectionRate: tech.detectionRate || 0
    }));

    return {
      _svgContent: reportChartService.generateMitreHeatmap(techniques, { theme }),
      summary: `${techniques.length} unique techniques tested across ${new Set(techniques.map(t => t.tactic)).size} tactics`,
      techniques
    };
  },

  // Generate Kill Chain SVG section for embedding in reports (uses per-technique data like web version)
  formatKillChainSection(debrief, theme = 'light') {
    // Use mitreAnalysis for individual technique nodes (matches the web kill chain view)
    const techniques = (debrief.mitreAnalysis || []).map(tech => ({
      technique: tech.technique,
      id: tech.technique,
      name: tech.name,
      tactic: tech.tactic,
      attempts: tech.attempts,
      successful: tech.successful,
      successRate: tech.successRate || 0,
      detectionRate: tech.detectionRate || 0,
      actions: tech.actions || []
    }));

    const activeTactics = new Set(techniques.map(t => t.tactic)).size;
    const totalActions = techniques.reduce((sum, t) => sum + (t.attempts || 0), 0);
    const totalSuccess = techniques.reduce((sum, t) => sum + (t.successful || Math.round((t.successRate || 0) * (t.attempts || 0))), 0);
    const overallRate = totalActions > 0 ? Math.round((totalSuccess / totalActions) * 100) : 0;

    return {
      _svgContent: reportChartService.generateKillChainSvg(techniques, { theme, title: 'Attack Kill Chain' }),
      summary: `${techniques.length} techniques across ${activeTactics} tactics with ${totalActions} total actions (${overallRate}% attacker success rate)`
    };
  },

  formatTrendData(debrief) {
    return {
      _chartType: 'bar-chart-js',
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      datasets: [
        {
          label: 'Detection Rate (%)',
          data: [45, 52, 58, Math.round(debrief.statistics.detectionRate * 100)]
        },
        {
          label: 'Critical Findings',
          data: [8, 6, 4, debrief.findings.critical.length]
        }
      ]
    };
  }
};

export default reportTemplatesService;
